import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, Mic } from 'lucide-react';

interface VoiceNotePlayerProps {
  audioUrl: string;
  durationSeconds?: number;
  isMe?: boolean;
}

export const VoiceNotePlayer: React.FC<VoiceNotePlayerProps> = ({
  audioUrl,
  durationSeconds = 0,
  isMe = false,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(durationSeconds);
  const [playbackRate, setPlaybackRate] = useState<1 | 1.5 | 2>(1);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoadedMetadata = () => {
      if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
        setTotalDuration(Math.round(audio.duration));
      }
    };

    const onTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
    };
  }, [audioUrl]);

  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.playbackRate = playbackRate;
      audio.play().then(() => setIsPlaying(true)).catch((err) => console.warn('Audio play failed:', err));
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const targetTime = parseFloat(e.target.value);
    setCurrentTime(targetTime);
    if (audioRef.current) {
      audioRef.current.currentTime = targetTime;
    }
  };

  const cyclePlaybackRate = (e: React.MouseEvent) => {
    e.stopPropagation();
    const rates: Array<1 | 1.5 | 2> = [1, 1.5, 2];
    const nextIdx = (rates.indexOf(playbackRate) + 1) % rates.length;
    const nextRate = rates[nextIdx];
    setPlaybackRate(nextRate);
    if (audioRef.current) {
      audioRef.current.playbackRate = nextRate;
    }
  };

  const formatSeconds = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const progressPercent = totalDuration > 0 ? Math.min(100, (currentTime / totalDuration) * 100) : 0;

  return (
    <div
      className={`flex items-center space-x-2.5 p-2 rounded-2xl min-w-[210px] sm:min-w-[260px] max-w-sm select-none transition-colors ${
        isMe
          ? 'bg-black/5 dark:bg-black/20 text-[#111b21] dark:text-[#e9edef]'
          : 'bg-surface-100/80 dark:bg-surface-800/80 text-surface-900 dark:text-surface-100'
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      <audio ref={audioRef} src={audioUrl} preload="metadata" />

      {/* Play / Pause circular button */}
      <button
        type="button"
        onClick={togglePlay}
        className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 shadow-sm transition-transform active:scale-95 bg-brand-600 hover:bg-brand-700 text-white"
        title={isPlaying ? 'Pause voice note' : 'Play voice note'}
      >
        {isPlaying ? <Pause className="w-4 h-4 fill-white" /> : <Play className="w-4 h-4 fill-white ml-0.5" />}
      </button>

      {/* Waveform / Scrubber Track */}
      <div className="flex-1 flex flex-col justify-center min-w-0">
        <div className="relative flex items-center h-4">
          {/* Simulated Waveform Bars background */}
          <div className="absolute inset-0 flex items-center justify-between pointer-events-none opacity-30 gap-0.5">
            {[40, 70, 30, 90, 60, 45, 80, 100, 50, 75, 35, 95, 60, 85, 40, 70, 55, 90, 65, 45].map((h, i) => (
              <span
                key={i}
                className={`w-[2.5px] rounded-full ${i * 5 <= progressPercent ? 'bg-brand-600 dark:bg-brand-400 opacity-100' : 'bg-surface-400'}`}
                style={{ height: `${h}%` }}
              />
            ))}
          </div>

          <input
            type="range"
            min={0}
            max={totalDuration || 1}
            step={0.1}
            value={currentTime}
            onChange={handleSeek}
            className="w-full h-1.5 opacity-0 cursor-pointer z-10"
          />
        </div>

        {/* Timestamp and Mic indicator */}
        <div className="flex items-center justify-between text-[10px] opacity-75 mt-0.5">
          <span>{isPlaying ? formatSeconds(currentTime) : formatSeconds(totalDuration)}</span>
          <div className="flex items-center space-x-1">
            <Mic className="w-2.5 h-2.5 text-brand-500" />
            <span>Voice</span>
          </div>
        </div>
      </div>

      {/* Speed Multiplier Button (1x, 1.5x, 2x) */}
      <button
        type="button"
        onClick={cyclePlaybackRate}
        className={`px-1.5 py-0.5 text-[10px] font-bold rounded-md shrink-0 transition-colors ${
          playbackRate > 1
            ? 'bg-brand-600 text-white'
            : isMe
            ? 'bg-black/10 dark:bg-white/10 text-inherit hover:bg-black/15'
            : 'bg-surface-200 dark:bg-surface-700 text-surface-700 dark:text-surface-300 hover:bg-surface-300'
        }`}
        title="Playback speed"
      >
        {playbackRate}x
      </button>
    </div>
  );
};
